/**
 * dossierNaming — DIAN normative file naming for ZIP export.
 *
 * Maps every MASTER_DOC_TYPE to a numeric prefix (01..11) so the documents
 * appear in the correct legal order when extracted. Items uploaded as
 * compliance support (archivos_soporte_cargados) live in a sub-folder.
 */
import { normaliseTipo } from "./documentTypes";

export interface DossierFileRule {
  /** Numeric prefix used for filename ordering (e.g. "01", "03"). */
  prefix: string;
  /** Human-readable normative name. */
  baseName: string;
  /** Sub-folder relative to the ZIP root. Empty = root. */
  folder: "" | "Soportes_Cumplimiento";
}

const RULES: Array<{ match: string; rule: DossierFileRule }> = [
  { match: "declaración de importación", rule: { prefix: "01", baseName: "Declaracion_Importacion", folder: "" } },
  { match: "declaración andina de valor", rule: { prefix: "02", baseName: "Declaracion_Andina_Valor", folder: "" } },
  { match: "factura internacional", rule: { prefix: "03", baseName: "Factura_Comercial", folder: "" } },
  { match: "factura de compras", rule: { prefix: "04", baseName: "Factura_Compra", folder: "" } },
  { match: "packing list", rule: { prefix: "05", baseName: "Packing_List", folder: "" } },
  { match: "bl", rule: { prefix: "06", baseName: "Documento_Transporte_BL", folder: "" } },
  { match: "guía aérea", rule: { prefix: "06", baseName: "Documento_Transporte_AWB", folder: "" } },
  { match: "guia aerea", rule: { prefix: "06", baseName: "Documento_Transporte_AWB", folder: "" } },
  { match: "documento de puerto", rule: { prefix: "06", baseName: "Documento_Puerto", folder: "" } },
  { match: "certificado de origen", rule: { prefix: "07", baseName: "Certificado_Origen", folder: "" } },
  { match: "póliza de seguro", rule: { prefix: "08", baseName: "Poliza_Seguro", folder: "" } },
  { match: "poliza de seguro", rule: { prefix: "08", baseName: "Poliza_Seguro", folder: "" } },
  { match: "certificado técnico", rule: { prefix: "09", baseName: "Certificado_Tecnico", folder: "Soportes_Cumplimiento" } },
  { match: "certificado tecnico", rule: { prefix: "09", baseName: "Certificado_Tecnico", folder: "Soportes_Cumplimiento" } },
  { match: "orden de compra", rule: { prefix: "10", baseName: "Orden_Compra", folder: "" } },
];

const FALLBACK: DossierFileRule = { prefix: "99", baseName: "Anexo", folder: "Soportes_Cumplimiento" };

/** Slugify text for filename usage: ASCII, no spaces, conservative chars. */
export function slugifyForFile(text: string | null | undefined, maxLen = 40): string {
  if (!text) return "";
  return text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, maxLen);
}

/** Extract file extension from URL/path. Defaults to "pdf". */
export function extractExtension(urlOrPath: string | null | undefined): string {
  if (!urlOrPath) return "pdf";
  const clean = urlOrPath.split("?")[0].split("#")[0];
  const last = clean.substring(clean.lastIndexOf("/") + 1);
  const dot = last.lastIndexOf(".");
  if (dot === -1 || dot === last.length - 1) return "pdf";
  const ext = last.slice(dot + 1).toLowerCase();
  // Whitelist common doc extensions; otherwise default to pdf.
  if (/^(pdf|jpg|jpeg|png|webp|tif|tiff|xml|xlsx|xls|docx|doc|csv|txt|zip)$/.test(ext)) {
    return ext;
  }
  return "pdf";
}

/** Lookup the rule for a given tipo_documento. */
export function getDossierRule(tipoDocumento: string | null | undefined): DossierFileRule {
  const norm = normaliseTipo(tipoDocumento);
  if (!norm) return FALLBACK;
  const hit = RULES.find((r) => norm === r.match || norm.includes(r.match));
  return hit ? hit.rule : FALLBACK;
}

/**
 * Build a normative filename for a document inside the ZIP.
 *
 * @param tipoDocumento  Document type from registro_documentos.
 * @param noDocumento    Document number (e.g. invoice no.).
 * @param archivoUrl     Original file URL/path (used for extension).
 * @param sequence       Sequence number when multiple docs share same prefix.
 */
export function buildDossierFileName(
  tipoDocumento: string | null | undefined,
  noDocumento: string | null | undefined,
  archivoUrl: string | null | undefined,
  sequence?: number,
): { folder: string; fileName: string; rule: DossierFileRule } {
  const rule = getDossierRule(tipoDocumento);
  const ext = extractExtension(archivoUrl);
  const slugDoc = slugifyForFile(noDocumento);
  const seq = sequence && sequence > 1 ? `_${sequence}` : "";
  const docPart = slugDoc ? `_${slugDoc}` : "";
  const fileName = `${rule.prefix}_${rule.baseName}${docPart}${seq}.${ext}`;
  return { folder: rule.folder, fileName, rule };
}

/** Build root folder name inside the ZIP: Dossier_DIAN_{DO}_{YYYYMMDD}. */
export function buildDossierRootFolder(numeroDo: string): string {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `Dossier_DIAN_${slugifyForFile(numeroDo) || "EMBARQUE"}_${yyyy}${mm}${dd}`;
}

/** Final ZIP file name (downloaded by browser). */
export function buildZipFileName(numeroDo: string): string {
  return `${buildDossierRootFolder(numeroDo)}.zip`;
}
